import * as React from "react";

const { useState, useEffect, useRef } = React;
import axios from "axios";
import { NotificationManager } from "react-notifications";

import { notifyServerErrorOrDefault, notifyServerErrorOrDefault2 } from "../helpers";
import { GInput } from "../components/GInput/GInput";

import "./event-editor";

interface IEventEditorProps {
    events: string[];
    isGlobalMod: boolean;
}

export const EventEditor: React.FC<IEventEditorProps> = (props: IEventEditorProps) => {

    const { isGlobalMod } = props;

    /** event creation */
    const [ createEventData, setCreateEventData ] = useState(null);
    const [ creationFormValid, setCreationFormValid ] = useState(false);

    const eventTypeRef = useRef(null);
    const locationRef = useRef(null);
    const countryCodeRef = useRef(null);
    const cityRef = useRef(null);
    const regionRef = useRef(null);
    const yearRef = useRef(null);
    const monthRef = useRef(null);

    // helpers
    const isTypeSelected = () => !!(createEventData && createEventData.eventType);
    const isConstrainedEventFormat = () => isTypeSelected() &&
        (
            //"Mission Day" == createEventData.eventType || "First Saturday" == createEventData.eventType || createEventData.eventType.startsWith("GORUCK:")
            "Mission Day" == createEventData.eventType ||
            "First Saturday" == createEventData.eventType ||
            "Operation Clear Field" == createEventData.eventType ||
            createEventData.eventType.startsWith("GORUCK:")
        );
    const validateRef = ref => ref.current ? ref.current.isValid() : false;

    useEffect(() => {
        // run validations of the form
        let relevantRefs;
        if (isConstrainedEventFormat()) {
            relevantRefs = [ eventTypeRef, countryCodeRef, cityRef, regionRef, yearRef, monthRef ];
        }
        else {
            relevantRefs = [ eventTypeRef, locationRef ];
        }
        setCreationFormValid(relevantRefs.every(validateRef));
    }, [ createEventData ]);


    const buildAddEventName = ({ location, countryCode, city, region, year, month }) => {
        if (isConstrainedEventFormat()) {
            return `${ (countryCode || "").toUpperCase() }${ city ? `-${ city }` : "" }${ region ? `, ${ region }` : "" } ${ year || "" }${ month ? `-${ month }` : "" }`;
        }
        return location;
    };

    const handleAddEvent = async (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        const { eventType } = createEventData,
            newEventLocation = buildAddEventName(createEventData),
            newEventName = `${ eventType } ${ newEventLocation }`;

        if (!confirm(`Add event "${ newEventName }"?`)) { return; }

        let response;
        try {
            response = axios.post("/backend/toolbox/event", {
                type: eventType, location: newEventLocation
            });
            await response;
            NotificationManager.success(`Created event "${ newEventName }"`);
            clearCreateEventForm();
        } catch (err) {
            notifyServerErrorOrDefault2(response, `Could not create event "${ newEventName }"`);
        }
    };

    const handleUpdateEvent = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
        e.preventDefault();

        const { id: eventId, location: eventLocation, type: eventType } = editEventData,
            newEventName = `${ eventType } ${ eventLocation }`;

        if (!confirm(`Update event #${ eventId } => "${ newEventName }"?`)) { return; }

        let response;
        try {
            response = axios.put("/backend/toolbox/event", {
                id: eventId,
                location: eventLocation
            });
            await response;
            NotificationManager.success(`Updated event #${ eventId } => "${ newEventName }"`);
            setEventToEdit(null);
            setEditEventData(null);
        } catch (err) {
            notifyServerErrorOrDefault2(response, `Could not update event #${ eventId  } to "${ newEventName }"`);
        }
    };

    const clearCreateEventForm = () => {
        setCreateEventData(null);
        [ eventTypeRef, locationRef, countryCodeRef, cityRef, regionRef, yearRef, monthRef ].forEach(r => {
            if (!r.current) {
                return;
            }
            r.current.clear();
        });
    };

    const renderCreateEventForm = () => {
        if (!isTypeSelected()) { return null; }
        if (isConstrainedEventFormat()) {
            return renderConstrainedFormatForm();
        }
        return renderSimpleForm();
    };

    const renderSimpleForm = () => {
        return (
            <div className="event-editor__constrained-event-format">
                <div className="event-editor__constrained-event-format-fields">
                    <GInput
                        label="Location"
                        innerClass="form-control"
                        onChange={ location => {
                            setCreateEventData({ ...createEventData, location });
                        } }
                        ref={ locationRef }
                        mandatory={ true }
                    />
                </div>
                <div className="event-editor__constrained-event-format-result">
                    <div className="event-editor__constrained-event-format-result-inner">
                        <div className="event-editor__constrained-event-format-preview">
                            Event name: <b>{ createEventData.eventType } { createEventData.location }</b>
                        </div>
                        <div className="event-editor__constrained-event-format-buttons">
                            <button
                                className="btn btn-primary"
                                type="submit"
                                disabled={ !creationFormValid }
                            >
                                Save
                            </button>
                            &nbsp;
                            <button type="button" className="btn btn-default"
                                onClick={ (e) => {
                                    e.preventDefault();
                                    clearCreateEventForm();
                                } }
                            >
                                Cancel
                            </button>
                        </div>

                    </div>
                </div>
            </div>
        );
    };

    const renderConstrainedFormatForm = () => {
        if (!createEventData) { return null; }
        return (
            <div className="event-editor__constrained-event-format">
                <div className="event-editor__constrained-event-format-fields">
                    <GInput
                        label="Country code"
                        innerClass="form-control"
                        ref={ countryCodeRef }
                        onChange={ countryCode => {
                            setCreateEventData({ ...createEventData, countryCode });
                        } }
                        validators={ [
                            val => /^[a-zA-Z]{2}$/.test(val) ? null : "Give a two-letter country code, eg. SE"
                        ] }
                    />
                    <GInput
                        label="City"
                        innerClass="form-control"
                        ref={ cityRef }
                        onChange={ city => {
                            setCreateEventData({ ...createEventData, city });
                        } }
                        validators={ [ value => value ? null : "Must enter something here" ] }
                    />
                    <GInput
                        label="Region"
                        innerClass="form-control"
                        onChange={ region => {
                            setCreateEventData({ ...createEventData, region });
                        } }
                        ref={ regionRef }
                    />
                    <GInput label="Year"
                        innerClass="form-control"
                        ref={ yearRef }
                        onChange={
                            year => {
                                setCreateEventData({
                                    ...createEventData,
                                    year
                                });
                            }
                        }
                        validators={ [
                            val => /^20((1[2-9])|([2-9]\d))$/.test(val) ? null : "Give a year, 2012 or greater"
                        ] }
                    />

                    <GInput<{ name: string; nr: number }>
                        label="Month name"
                        innerClass="form-control"
                        ref={ monthRef }
                        items={ [ "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December" ].map((name, idx) => {
                            const nr = String(idx + 1).padStart(2, "0");
                            return ({ name, nr });
                        }) }
                        itemPropNames={ [ "name", "nr" ] }
                        onChange={
                            (item) => {
                                const month = null == item ? null : item.nr;
                                setCreateEventData({
                                    ...createEventData,
                                    month
                                });
                            }
                        }
                        mandatory={ true }
                    />
                </div>


                <div className="event-editor__constrained-event-format-result">
                    <div className="event-editor__constrained-event-format-result-inner">
                        <div className="event-editor__constrained-event-format-preview">
                            <b>{ createEventData.eventType }: { buildAddEventName(createEventData) }</b>
                        </div>
                        <div className="event-editor__constrained-event-format-buttons">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={ !creationFormValid }
                            >
                                Add event
                            </button>
                            &nbsp;
                            <button type="button" className="btn btn-default"
                                onClick={ (e) => {
                                    e.preventDefault();
                                    clearCreateEventForm();
                                } }
                            >
                                Cancel
                            </button>
                        </div>

                    </div>
                </div>

            </div>
        );
    };


    /** event editing */
    const [ eventToEdit, setEventToEdit ] = useState(null);
    const searchEventToEditInput = useRef(null);

    const doDeleteEvent = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const { id, name, location } = eventToEdit,
            eventName = `${ name }: ${ location }`;
        if (!confirm(`Delete event "${ eventName }"?`)) { return; }
        let response;
        try {
            response = await axios.delete(`/backend/toolbox/event/${ id }`);
            setEventToEdit(null);
            NotificationManager.success(`Deleted event "${ eventName }"`);
        } catch (err) {
            notifyServerErrorOrDefault(response, `Could not delete event "${ eventName }"`);
        }
    };

    const [ editEventData, setEditEventData ] = useState(null);
    const isEditMode = () => editEventData != null;
    const doEditEvent = (e: React.SyntheticEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setEditEventData({
            id: eventToEdit.id,
            type: eventToEdit.name,
            location: eventToEdit.location,
        });
    };

    const renderEventEditForm = () => {
        return (
            <div className="event-editor-event-edit-form">
                <div className="foo">
                    <GInput innerClass="form-control"
                        label="New location"
                        initialValue={ editEventData.location }
                        mandatory={ true }
                        onChange={ location => {
                            setEditEventData({
                                ...editEventData,
                                location
                            });
                        } }
                    />
                </div>
                <div className="bar">
                    <div className="event-editor-event-edit-form__preview">
                        Event id: #{ editEventData.id }<br />
                        Event name: <b>{ editEventData.type } { editEventData.location }</b>
                    </div>
                    <div className="event-editor-event-edit-form__buttons">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={ null == eventToEdit }
                            onClick={ handleUpdateEvent }
                        >
                            Save
                        </button>
                        &nbsp;&nbsp;
                        <button
                            onClick={ () => {
                                setEventToEdit(null);
                                setEditEventData(null);
                            } }
                            className="btn btn-default"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderEventDeleteForm = () => {
        return (
            <div className="event-editor-event-delete-form">
                <div>
                    #{ eventToEdit.id } - <b>{ eventToEdit.fullname }</b>
                </div>
                <div className="event-editor-event-delete-form__buttons">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={ null == eventToEdit }
                        onClick={ doEditEvent }
                    >
                        Edit
                    </button>
                    { isGlobalMod ?
                        <button
                            type="submit"
                            className="btn btn-danger"
                            disabled={ null == eventToEdit }
                            onClick={ doDeleteEvent }
                        >
                            Delete
                        </button> : null
                    }
                    <button
                        onClick={ () => {
                            setEventToEdit(null);
                            setEditEventData(null);
                        } }
                        className="btn btn-default"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    };
    const renderEditEvent = () => {
        if (!eventToEdit) { return null; }
        if (isEditMode()) {
            return renderEventEditForm();
        }
        return renderEventDeleteForm();
    };

    return (
        <div>
            <div className="row" id="header">
                <div className="col-xs-12">
                    <h2>Event editor</h2>
                </div>
            </div>

            <div className="row">
                <div className="col-xs-12">
                    <h3>Add event</h3>
                </div>
            </div>

            <div className="row">
                <div className="col-xs-12">
                    <form onSubmit={ handleAddEvent }>
                        <div className="form-group">
                            <GInput label="Event type"
                                innerClass="form-control"
                                items={ props.events }
                                onChange={ eventType => {
                                    setCreateEventData({ ...createEventData, eventType });
                                } }
                                ref={ eventTypeRef }
                                mandatory={ true }
                            />
                        </div>
                        <div>
                            { renderCreateEventForm() }
                        </div>
                    </form>
                </div>
            </div>

            <div className="row">
                <div className="col-xs-12">
                    <hr/>
                </div>
            </div>

            <div className="row">
                <div className="col-xs-12">
                    <h3>Edit Event</h3>
                </div>
            </div>
            <div className="row">
                <div className="col-xs-12">
                    <div className="form-group">
                        <div>
                            {
                                eventToEdit ? null :
                                    <GInput
                                        ref={ searchEventToEditInput }
                                        innerClass="form-control"
                                        label="Event"
                                        placeholder="Type an event name"
                                        remote={ {
                                            url: "/backend/event",
                                            paramName: "q"
                                        } }
                                        paramName="q"
                                        onChange={ item => {
                                            setEventToEdit(item);
                                            setEditEventData(null);
                                        } }
                                        itemPropNames={ [ "fullname" ] }
                                    />
                            }
                            { renderEditEvent() }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

